import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  CandlestickChart, Globe, Landmark, Layers, LayoutGrid,
  ReceiptText, Ticket, Timer, Trophy, type LucideIcon,
} from 'lucide-react';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { Icon } from '../components/ui/icon.js';
import { GameCard, type GameLike } from '../components/lobby/GameCard.js';
import { EmptyState } from '../components/lobby/EmptyState.js';
import { GamesGridSkeleton } from '../components/lobby/GamesGridSkeleton.js';
import { SiteFooter } from '../components/site/SiteFooter.js';
import { useAuthStore } from '../stores/auth.js';
import { fetchGames, GAMES_QUERY_KEYS, type Game } from '../features/games/api.js';
import { sortLotteryFirst } from '../features/games/lotterySort.js';

// Public — unauth users browse, login redirect happens on play.
export const Route = createFileRoute('/_player/lottery/')({
  component: LotteryPage,
});

// Preferred display order for the type pills; any type not listed sorts last.
const LOTTERY_TYPE_ORDER = ['YEEKEE', 'STOCK', 'MAIN', 'SET', 'GOVERNMENT'];
// Fallback labels for the `casino:lotteryType.*` i18n keys — used only if a key
// is absent (unknown SUPERM type) so a pill never renders a raw code.
const LOTTERY_TYPE_LABEL: Record<string, string> = {
  YEEKEE: 'ยี่กี',
  STOCK: 'หวยหุ้น',
  MAIN: 'ต่างประเทศ',
  SET: 'หวยชุด',
  GOVERNMENT: 'รัฐบาล',
};
const LOTTERY_TYPE_ICON: Record<string, LucideIcon> = {
  YEEKEE: Timer,
  STOCK: CandlestickChart,
  MAIN: Globe,
  SET: Layers,
  GOVERNMENT: Landmark,
};

function LotteryPage() {
  const { t } = useTranslation(['casino', 'common']);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Stable identity — GameCard is memoized; an inline arrow here would force
  // all 120 cards to re-render on every keystroke/refetch of this page.
  const onPlay = useCallback(
    (game: GameLike) => {
      if (!accessToken) {
        void navigate({ to: '/login' });
        return;
      }
      if (!game.code) return;
      void navigate({ to: '/lottery/$code', params: { code: game.code } });
    },
    [accessToken, navigate],
  );

  // Lottery rounds rotate ~15 min — refetch in the foreground so countdown
  // badges and AWAITING → CLOSED transitions stay current.
  const gamesQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.list({ category: 'lottery' }),
    queryFn: () => fetchGames({ category: 'lottery', pageSize: 200 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const allItems = useMemo(() => (gamesQuery.data?.items ?? []) as Game[], [gamesQuery.data]);

  // Distinct lottery types present → drive the filter pills (built from data, so
  // a new SUPERM type appears automatically without a code change).
  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of allItems) {
      const tp = g.lotteryType ?? '';
      if (tp) m.set(tp, (m.get(tp) ?? 0) + 1);
    }
    return m;
  }, [allItems]);

  const types = useMemo(() => {
    const present = [...typeCounts.keys()];
    const rank = (x: string) => {
      const i = LOTTERY_TYPE_ORDER.indexOf(x);
      return i < 0 ? LOTTERY_TYPE_ORDER.length : i;
    };
    return present.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [typeCounts]);

  const games = useMemo(() => {
    let items = allItems.slice();
    if (typeFilter !== 'all') items = items.filter((g) => g.lotteryType === typeFilter);
    if (q.trim()) items = items.filter((g) => g.name.toLowerCase().includes(q.trim().toLowerCase()));
    return sortLotteryFirst(items);
  }, [allItems, typeFilter, q]);

  const pillCls = (active: boolean) =>
    [
      'inline-flex items-center gap-1.5 h-11 px-5 rounded-xl text-[14px] font-bold whitespace-nowrap transition-colors',
      active
        ? 'bg-[var(--accent)] text-[var(--accent-on)] shadow-[0_6px_18px_var(--accent-glow)]'
        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)]',
    ].join(' ');

  const quickBtnCls =
    'inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[var(--bg-raised)] px-4 text-[13px] font-bold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]';

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <Icon name="ticket" size={26} className="text-[var(--accent-strong)]" />
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t('tabs.lottery')}</h1>
            <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
              {t('gameCount', { count: games.length, total: gamesQuery.data?.total ?? '…' })}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Link to="/lottery/tickets" className={quickBtnCls}>
              <ReceiptText size={16} />
              {t('lottery.myTickets')}
            </Link>
            <Link to="/lottery/results" className={quickBtnCls}>
              <Trophy size={16} />
              {t('lottery.results')}
            </Link>
          </div>
          <div className="sm:w-64 lg:w-72">
            <Input
              prefix={<Icon name="search" size={16} className="text-[var(--text-muted)]" />}
              placeholder={t('search.placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      {types.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bf-noscroll -mx-1 px-1">
          <button type="button" onClick={() => setTypeFilter('all')} className={pillCls(typeFilter === 'all')}>
            <LayoutGrid size={15} />
            {t('common:common.all', { defaultValue: 'ทั้งหมด' })}
            <span className="tabular-nums opacity-60">{allItems.length}</span>
          </button>
          {types.map((tp) => {
            const TypeIcon = LOTTERY_TYPE_ICON[tp] ?? Ticket;
            return (
              <button key={tp} type="button" onClick={() => setTypeFilter(tp)} className={pillCls(typeFilter === tp)}>
                <TypeIcon size={15} />
                {t(`lotteryType.${tp}`, { defaultValue: LOTTERY_TYPE_LABEL[tp] ?? tp })}
                <span className="tabular-nums opacity-60">{typeCounts.get(tp)}</span>
              </button>
            );
          })}
        </div>
      )}

      {gamesQuery.isPending ? (
        <GamesGridSkeleton count={10} aspect="aspect-[8/5]" />
      ) : gamesQuery.isError ? (
        <EmptyState
          icon="rotate"
          title={t('loadError.title')}
          body={t('loadError.body')}
          action={
            <Button size="sm" variant="secondary" onClick={() => void gamesQuery.refetch()}>
              {t('common:common.retry')}
            </Button>
          }
        />
      ) : games.length === 0 ? (
        <EmptyState icon="search" title={t('empty.title')} body={t('empty.body')} />
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
          {games.map((g) => (
            // content-visibility lets the browser skip layout/paint for the
            // ~100 cards below the fold (blurred flags + pulse dots are the
            // expensive part); intrinsic-size keeps the scrollbar stable.
            <div key={g.id} className="[content-visibility:auto] [contain-intrinsic-size:auto_280px]">
              <GameCard game={g as never} onPlay={onPlay} />
            </div>
          ))}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
